import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { ArrowLeft, Shield, FileText, Mail, Phone, Building2, Scale, Lock, Users, Clock, ExternalLink } from 'lucide-react';

const Section = ({ icon: Icon, title, children }) => (
  <section className="mb-8">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-xl bg-[#374B67]/10 flex items-center justify-center flex-shrink-0">
        <Icon size={18} className="text-[#374B67]" />
      </div>
      <h2 className="text-lg font-semibold text-[#374B67]">{title}</h2>
    </div>
    <div className="text-sm text-slate-700 leading-relaxed space-y-2 pl-12">{children}</div>
  </section>
);

export default function PoliticaPrivacidad() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Back */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#374B67] transition-colors mb-6"
        >
          <ArrowLeft size={16} />
          Volver al inicio
        </Link>

        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-[#374B67] to-[#2A3B52] text-white p-6 sm:p-8 mb-8 shadow-md">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
              <Shield size={26} className="text-[#69C7EA]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#69C7EA] font-semibold">RGPD · LOPDGDD</p>
              <h1 className="text-xl sm:text-2xl font-bold">Política de Privacidad</h1>
            </div>
          </div>
          <p className="text-sm text-slate-300 leading-relaxed">
            Servicio municipal de Recogida de Enseres Voluminosos<br />
            Ayuntamiento de Majadahonda — gestionado por Valoriza Servicios Medioambientales, S.A.
          </p>
          <p className="text-xs text-slate-400 mt-3">
            Última actualización: mayo de 2025 · En cumplimiento del Reglamento (UE) 2016/679 (RGPD)
            y la Ley Orgánica 3/2018, de 5 de diciembre (LOPDGDD)
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur-sm shadow-sm p-6 sm:p-8 space-y-0">

          {/* 1. Responsable */}
          <Section icon={Building2} title="1. Responsable del tratamiento">
            <p>
              El <strong>Ayuntamiento de Majadahonda</strong> actúa como responsable del tratamiento de sus datos
              personales en el marco de la prestación del servicio municipal de recogida de enseres voluminosos.
            </p>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mt-3 space-y-1">
              <p><strong>Entidad:</strong> Ayuntamiento de Majadahonda</p>
              <p><strong>CIF:</strong> P2815000-D</p>
              <p><strong>Dirección:</strong> Plaza del Pueblo, 1 · 28220 Majadahonda (Madrid)</p>
              <p><strong>Email DPD:</strong>{' '}
                <a href="mailto:dpd@majadahonda.es" className="text-[#374B67] hover:underline">
                  dpd@majadahonda.es
                </a>
              </p>
            </div>
            <p className="mt-3">
              El servicio es prestado por <strong>Valoriza Servicios Medioambientales, S.A.</strong> en calidad
              de encargado del tratamiento, de conformidad con el artículo 28 del RGPD, bajo las instrucciones
              del Ayuntamiento.
            </p>
          </Section>

          {/* 2. Finalidad */}
          <Section icon={FileText} title="2. Finalidad del tratamiento">
            <p>Sus datos personales son tratados con las siguientes finalidades:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li>
                <strong>Gestión y tramitación</strong> de su solicitud de recogida de enseres voluminosos:
                asignación de fecha, zona, vehículo y ruta de recogida.
              </li>
              <li>
                <strong>Notificaciones</strong> sobre el estado de su solicitud (confirmación, cambios de estado,
                aviso de recogida) mediante correo electrónico y/o WhatsApp, según el canal que usted elija.
              </li>
              <li>
                <strong>Estadísticas y planificación</strong> del servicio municipal, de forma anonimizada.
              </li>
              <li>
                <strong>Cumplimiento de obligaciones legales</strong> derivadas de la normativa de residuos y
                servicios municipales.
              </li>
            </ul>
          </Section>

          {/* 3. Legitimación */}
          <Section icon={Scale} title="3. Base jurídica del tratamiento">
            <p>
              El tratamiento está legitimado por el artículo 6.1.e) del RGPD —<em>«tratamiento necesario para
              el cumplimiento de una misión realizada en interés público o en el ejercicio de poderes
              públicos»</em>— en relación con la Ley 7/1985 Reguladora de las Bases del Régimen Local, que
              atribuye a los municipios la competencia en materia de recogida de residuos.
            </p>
            <p>
              La comunicación de notificaciones por WhatsApp está adicionalmente basada en su consentimiento
              (art. 6.1.a), que puede retirar en cualquier momento sin que ello afecte a la tramitación de
              su solicitud.
            </p>
          </Section>

          {/* 4. Datos tratados */}
          <Section icon={Users} title="4. Datos personales tratados">
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[#374B67]/8 text-[#374B67]">
                    <th className="text-left p-2 border border-slate-200 rounded-tl-lg">Dato</th>
                    <th className="text-left p-2 border border-slate-200">Obligatorio</th>
                    <th className="text-left p-2 border border-slate-200 rounded-tr-lg">Uso</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Nombre y apellidos', 'Sí', 'Identificación del solicitante'],
                    ['Correo electrónico', 'Sí (o teléfono)', 'Notificaciones de estado'],
                    ['Teléfono / WhatsApp', 'Sí (o email)', 'Notificaciones de estado'],
                    ['Dirección de recogida', 'Sí', 'Planificación de ruta'],
                    ['Coordenadas GPS', 'Sí', 'Asignación de zona y ruta'],
                    ['Fotografía del enser', 'No', 'Estimación de volumen (IA)'],
                    ['Descripción de enseres', 'Sí', 'Planificación y trazabilidad'],
                  ].map(([d, o, u], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-2 border border-slate-200 font-medium">{d}</td>
                      <td className="p-2 border border-slate-200">{o}</td>
                      <td className="p-2 border border-slate-200 text-slate-600">{u}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              No se tratan datos especialmente protegidos (art. 9 RGPD). No se realizan decisiones automatizadas
              con efectos jurídicos. El análisis de imagen por IA se realiza exclusivamente para estimar el
              volumen del enser; la fotografía no es almacenada en ningún repositorio externo de IA.
            </p>
          </Section>

          {/* 5. Destinatarios */}
          <Section icon={Users} title="5. Destinatarios y transferencias">
            <p>Sus datos podrán ser comunicados a:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>
                <strong>Valoriza Servicios Medioambientales, S.A.</strong>, encargada del tratamiento para
                la ejecución del servicio de recogida.
              </li>
              <li>
                <strong>Twilio Ireland Ltd.</strong> (proveedor de mensajería WhatsApp), bajo contrato de
                encargado del tratamiento y con transferencia internacional basada en Cláusulas Contractuales
                Estándar aprobadas por la Comisión Europea. Sólo aplicable si usted activa el canal WhatsApp.
              </li>
              <li>
                <strong>Organismos públicos</strong> cuando sea exigido por obligación legal.
              </li>
            </ul>
            <p className="mt-2">
              No se realizan transferencias internacionales fuera del Espacio Económico Europeo, salvo la
              indicada con Twilio.
            </p>
          </Section>

          {/* 6. Conservación */}
          <Section icon={Clock} title="6. Plazo de conservación">
            <p>
              Sus datos serán conservados durante el período necesario para la tramitación de la solicitud y
              durante <strong>5 años</strong> adicionales para cumplir con las obligaciones de responsabilidad
              y auditoría de servicios municipales, salvo que una norma especial exija un plazo distinto.
            </p>
            <p>
              Una vez expirado el plazo, los datos serán suprimidos o anonimizados de forma irrevocable.
            </p>
          </Section>

          {/* 7. Derechos */}
          <Section icon={Lock} title="7. Sus derechos">
            <p>
              En virtud del RGPD y la LOPDGDD, usted puede ejercer los siguientes derechos:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {[
                ['Acceso', 'Saber qué datos tenemos sobre usted'],
                ['Rectificación', 'Corregir datos inexactos'],
                ['Supresión', 'Solicitar la eliminación de sus datos'],
                ['Oposición', 'Oponerse al tratamiento'],
                ['Limitación', 'Restringir el uso de sus datos'],
                ['Portabilidad', 'Obtener sus datos en formato estructurado'],
              ].map(([d, desc]) => (
                <div key={d} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold text-[#374B67]">{d}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                </div>
              ))}
            </div>
            <p className="mt-3">
              Para ejercer sus derechos, envíe un escrito al <strong>Delegado de Protección de Datos (DPD)</strong> del
              Ayuntamiento de Majadahonda:
            </p>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mt-2 space-y-1">
              <p className="flex items-center gap-2">
                <Mail size={14} className="text-[#374B67]" />
                <a href="mailto:dpd@majadahonda.es" className="text-[#374B67] hover:underline">
                  dpd@majadahonda.es
                </a>
              </p>
              <p className="flex items-center gap-2">
                <Building2 size={14} className="text-[#374B67]" />
                Plaza del Pueblo, 1 · 28220 Majadahonda (Madrid)
              </p>
            </div>
            <p className="mt-3">
              Si considera que el tratamiento no es conforme con la normativa, puede presentar una
              reclamación ante la <strong>Agencia Española de Protección de Datos (AEPD)</strong>:
            </p>
            <a
              href="https://www.aepd.es"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[#374B67] hover:underline mt-1"
            >
              <ExternalLink size={13} />
              www.aepd.es
            </a>
          </Section>

          {/* 8. Seguridad */}
          <Section icon={Shield} title="8. Medidas de seguridad">
            <p>
              El Ayuntamiento de Majadahonda y Valoriza han adoptado las medidas técnicas y organizativas
              adecuadas para garantizar la seguridad, confidencialidad e integridad de sus datos personales,
              de acuerdo con el artículo 32 del RGPD, incluyendo cifrado en tránsito (HTTPS/TLS),
              control de acceso basado en roles y registros de auditoría.
            </p>
          </Section>

          {/* 9. Cookies */}
          <Section icon={FileText} title="9. Uso de cookies">
            <p>
              Este portal utiliza únicamente <strong>cookies técnicas estrictamente necesarias</strong> para
              el funcionamiento de la sesión y la gestión del formulario de solicitud. No se utilizan cookies
              de análisis, publicidad ni seguimiento de terceros.
            </p>
          </Section>

          {/* 10. Modificaciones */}
          <Section icon={FileText} title="10. Modificaciones de esta política">
            <p>
              El Ayuntamiento de Majadahonda se reserva el derecho a actualizar esta política cuando sea
              necesario por cambios normativos o en el servicio. La versión vigente estará siempre disponible
              en esta dirección. Le recomendamos consultarla periódicamente.
            </p>
          </Section>

        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-400 mt-8">
          Política de Privacidad · Servicio de Recogida de Enseres Voluminosos<br />
          Ayuntamiento de Majadahonda · Valoriza Servicios Medioambientales, S.A.
        </p>
      </div>
    </div>
  );
}
